import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { IconButton } from "@mui/material";
import JobStyles from "../../Job/JobStyle.module.css";

export default function JobReportDraggablePhotos({ files, basePath, report, updateOrder, disabled, onPhotoMove, onDrop }) {
  return files.map((card, cardIndex) => (
    <PhotoCard
      key={card.file_name}
      file={card}
      index={cardIndex}
      report={report}
      basePath={basePath}
      totalPhotos={files.length}
      moveCard={onPhotoMove}
      columns={3}
      updateOrder={updateOrder}
      disabled={disabled}
      onDrop={onDrop}
    />
  ));
}

const PhotoCard = ({ file, moveCard, basePath, report, index, totalPhotos, disabled, onDrop, columns }) => {
  const ref = useRef(null);

  const [{ handlerId }, drop] = useDrop(
    () => ({
      accept: "job_report_photos",

      canDrop: () => !disabled,

      collect: (monitor) => ({
        handlerId: monitor.getHandlerId(),
      }),

      drop: (item) => ({
        targetReportId: item.sourceReportId,
        targetIndex: item.sourceIndex,
      }),

      hover: (item, monitor) => {
        if (!ref.current) return;
        if (disabled) return;

        const dragIndex = item.sourceIndex;
        const hoverIndex = index;
        const dragReportId = item.sourceReportId;
        const hoverReportId = report.id;

        const sameReport = dragReportId === hoverReportId;

        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;

        const hoverRect = ref.current.getBoundingClientRect();

        const hoverMiddleX = (hoverRect.right - hoverRect.left) / 2;
        const hoverClientX = clientOffset.x - hoverRect.left;

        const isLastPhoto = hoverIndex === totalPhotos - 1;

        let thresholdX = hoverMiddleX;

        if (isLastPhoto) {
          thresholdX = (hoverRect.right - hoverRect.left) * 0.3;
        }

        let targetIndex = hoverIndex;

        if (hoverClientX > thresholdX) {
          targetIndex = hoverIndex + 1;
        } else {
          targetIndex = hoverIndex;
        }

        if (targetIndex < 0) targetIndex = 0;
        if (targetIndex > totalPhotos) targetIndex = totalPhotos;

        if (sameReport) {
          if (targetIndex === dragIndex) return;
          if (targetIndex === dragIndex + 1) return;
        }

        const hoverKey = hoverReportId + ":" + targetIndex;
        if (item.lastHoverKey === hoverKey) return;

        moveCard({
          fileId: item.fileId,
          sourceReportId: dragReportId,
          sourceIndex: dragIndex,
          targetReportId: hoverReportId,
          targetIndex: targetIndex,
        });

        item.lastHoverKey = hoverKey;
        item.hasMoved = true;

        let nextIndex = targetIndex;

        if (sameReport) {
          if (dragIndex < targetIndex) {
            nextIndex = targetIndex - 1;
          }
        }

        item.sourceReportId = hoverReportId;
        item.sourceIndex = nextIndex;
      }
    }),
    [disabled, index, report.id, moveCard]
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "job_report_photos",

      item: () => ({
        file,
        fileId: file.file_name,
        sourceReportId: report.id,
        sourceIndex: index,
        originalReportId: report.id,
        originalSourceIndex: index,
        hasMoved: false,
        lastHoverKey: null,
      }),

      end: (item, monitor) => {
        if (!item) return;
        if (!monitor.didDrop()) return;
        if (!item.hasMoved) return;

        const originalReportId = item.originalReportId;
        const originalSourceIndex = item.originalSourceIndex;
        const finalReportId = item.sourceReportId;
        const finalSourceIndex = item.sourceIndex;

        const sameReport = originalReportId === finalReportId;
        const sameIndex = originalSourceIndex === finalSourceIndex;

        if (sameReport && sameIndex) {
          return;
        }

        if (originalReportId !== finalReportId) {
          onDrop([originalReportId, finalReportId]);
        } else {
          onDrop([finalReportId]);
        }
      },

      canDrag: () => !disabled,

      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [disabled, file, index, report.id, onDrop]
  );
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`${JobStyles.ReportImageContainer} position-relative`}
      style={{ opacity: isDragging ? 0 : 1, cursor: disabled ? "unset" : "grab" }}>
      <div className="position-absolute">
        <IconButton
          disabled={disabled}
          style={{ height: "auto", cursor: disabled ? "unset" : "grab" }}
          className="p-0"
          data-handler-id={handlerId}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={JobStyles.DragIcon}
            style={{ height: "14px", cursor: disabled ? "unset" : "grab" }}
            viewBox="0 0 320 512">
            <path
              fill="currentColor"
              d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z"
            />
          </svg>
        </IconButton>
      </div>

      <img className={`${JobStyles.ReportImage} mx-auto`} src={basePath + file.file_name} alt="" />
    </div>
  );
};
